import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { get } from 'lodash-es';
import { useLocation } from 'react-router-dom';
import useErrorBoundary from 'use-error-boundary';
import useAuth from '../hooks/useAuth';

import Header from './Header';
import Footer from './Footer';
import SplashScreen from './SplashScreen';

import styles from './Layout.module.css';

const Layout = ({ children }) => {
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    canAccessApp,
    accessStatus,
    requestAccess,
    logout,
  } = useAuth();

  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [loading, setLoading] = useState(false);
  const shouldShowAccessCard = !canAccessApp;

  const accessMessageByStatus: Record<string, string> = {
    rejected: 'Your access was rejected. You can submit a new request for review.',
    pending: 'Your request is pending admin approval.',
    'not-requested': 'Please submit an access request to continue.',
    approved: 'You are approved to access the app.',
  };

  const accessMessage = accessMessageByStatus[accessStatus] || accessMessageByStatus['not-requested'];

  const {
    ErrorBoundary,
    didCatch: isJavascriptExceptionCaught,
    error: javascriptException
  } = useErrorBoundary();

  useEffect(() => {
    setLoading(isAuthLoading);
    setShowSplashScreen(!isAuthenticated);
  }, [isAuthenticated, isAuthLoading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (isJavascriptExceptionCaught) {
    return (
      <div className={styles.javascriptException}>
        Something went wrong: {get(javascriptException, 'message')}
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className={styles.container}>
        <SplashScreen
          loading={loading}
          setLoading={setLoading}
          isVisible={showSplashScreen}
        />
        {!showSplashScreen && (
          <>
            <Header />
            {shouldShowAccessCard ? (
              <div className={styles.accessCard}>
                <div className={styles.accessTitle}>Access Approval Required</div>
                <div className={styles.accessText}>{accessMessage}</div>
                <div className={styles.accessActions}>
                  <Button variant="contained" onClick={() => requestAccess()}>
                    {accessStatus === 'pending' ? 'Resend Request' : 'Request Access'}
                  </Button>
                  <Button variant="outlined" onClick={() => logout()}>
                    Logout
                  </Button>
                </div>
              </div>
            ) : (
              children
            )}
            <Footer />
          </>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Layout;
